import type { EventBusKey } from '@vueuse/core';
import type { Pagination } from '~/core/entities/api/ApiPagination';
import type { PropertyFilters } from '~/core/entities/property/filters';

/*
 * @constant
 * The default server pagination is used to store the initial values for the server pagination.
 */
export const DEFAULT_SERVER_PAGINATION: Pick<Pagination, 'page' | 'perPage'> = {
	page: 1,
	perPage: 24
};

/*
 * @constant
 * The default client pagination is used to store the initial values for the client pagination.
 */
export const DEFAULT_CLIENT_PAGINATION: Pick<
	Pagination,
	'page' | 'perPage' | 'filteredCount'
> = {
	page: 1,
	perPage: 10,
	filteredCount: 0
};

/*
 * Freezing the default pagination values to prevent any side effect.
 */
Object.freeze(DEFAULT_SERVER_PAGINATION);
Object.freeze(DEFAULT_CLIENT_PAGINATION);

export type ClientPagination = Pick<
	Pagination,
	'page' | 'perPage' | 'filteredCount'
>;
export type ServerPagination = Pick<Pagination, 'page' | 'perPage'>;

export const applyFiltersMixpanelKey: EventBusKey<{
	filters: PropertyFilters;
}> = Symbol('filters-mixpanel-key');

/*
 * The Pagination function is used to create a reactive pagination object.
 * The pagination object is used to store the pagination values and to provide a reactive way to update the pagination values.
 */
interface PaginationReturn {
	serverPagination: ComputedRef<ServerPagination>;
	clientPagination: ComputedRef<ClientPagination>;
	displayPagination: ComputedRef<ClientPagination>;
	nextServerPage: () => void;
	nextClientPage: () => void;
	nextDisplayPage: () => void;
	updateCount: (count: Pagination['filteredCount']) => void;
	reset: () => void;
}

export function Pagination(): PaginationReturn {
	const _pagination = useState('_properties-pagination', () => ({
		/*
		 * The server pagination is used to store the pagination used exclusively for requets to the server.
		 * Server pagination always should have perPage value of 24.
		 */
		server: { ...DEFAULT_SERVER_PAGINATION },
		/*
		 * The client pagination is used to store the pagination used for reactive changes on elements and components.
		 * Client pagination always should have perPage value of 10 or 12. 10 if for the first page and 12 for the next pages. So if the user is on the first page, the perPage value should be 10, otherwise 12.
		 * The filteredCount is used to store the total number of items that are being displayed on the page.
		 * Ever when pagination is reset, the filteredCount should be reset to 0 and perPage 10.
		 */
		client: { ...DEFAULT_CLIENT_PAGINATION },
		display: { ...DEFAULT_CLIENT_PAGINATION }
	}));
	/*
	 * The serverPagination is a computed value that returns the server pagination.
	 */
	const serverPagination = computed<ServerPagination>(
		() => _pagination.value.server
	);
	/*
	 * The clientPagination is a computed value that returns the client pagination.
	 */
	const clientPagination = computed(() => _pagination.value.client);

	const displayPagination = computed(() => _pagination.value.display);

	/*
	 * The nextClientPage function is used to increment the page value on server pagination.
	 */
	function nextServerPage() {
		_pagination.value.server.page += 1;
	}
	/*
	 * The nextClientPage function is used to increment the page value on client pagination.
	 */
	function nextClientPage() {
		_pagination.value.client.page += 1;
		_pagination.value.client.perPage = 12;
	}

	function nextDisplayPage() {
		_pagination.value.display.page += 1;
	}

	/*
	 * The updateCount function is used to update the filteredCount value on the client pagination.
	 * This function should be called when the data is fetched from the server and we have the new filtered count value.
	 */
	function updateCount(count: Pagination['filteredCount']) {
		_pagination.value.client.filteredCount = count;
	}
	/*
	 * The reset function is used to reset the pagination state to its initial values.
	 */
	function reset() {
		_pagination.value.server = { ...DEFAULT_SERVER_PAGINATION };
		_pagination.value.client = { ...DEFAULT_CLIENT_PAGINATION };
	}

	return {
		serverPagination,
		clientPagination,
		displayPagination,
		nextServerPage,
		nextClientPage,
		nextDisplayPage,
		updateCount,
		reset
	};
}
